import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import {
  DownCircleOutlined,
  TeamOutlined,
  PoweroffOutlined,
  MoreOutlined,
} from '@ant-design/icons';

import { Menu, MenuItem } from '@szhsin/react-menu';

import css from './InputTriggerCard.module.css';
import { InputTrigger } from '../use-input-triggers-identification';
import { ReviewBadge } from '../../DialogueEditor/NodeTypes/elements/ReviewBadge/ReviewBadge';
import { useSelector } from 'react-redux';
import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import PageModel from '@mm/state/model/PageModel';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { useAutomationActions } from '@mm/state/actions/automationActions';
import { useNeedsReview } from '../../use-needs-review';
import { selectPages } from '@mm/state/selectors/pageSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { emojis } from 'packages/constants/emojis/emojis';
import {
  ChannelIcon,
  ChannelType,
} from '../../../../../../liveChat/components/ChannelIcon';
import noImage from 'assets/no-post-image.svg';
import { ImageWithFallback } from '../../../../../../../../common/components/ImageWithFallback/ImageWithFallback';
import { Status } from '@mm/components';
import { XRayIconSquare as XRayIcon } from '@mm/icons';

type Props = {
  campaignId: number;
  type?: string;
  kind?: string;
  channel?: ChannelType;
  className?: string;
  title: string;
  tags?: string[] | null;
  info?: string;
  active?: boolean;
  showActivateAction?: boolean;
  error?: string;
  subtitle?: string;
  subtitleImage?: string;
  subtitleStyleLight?: boolean;
  pageId?: number;
  onClick?: InputTrigger['onClick'];
  onToggle?: InputTrigger['setActive'];
  onDelete: InputTrigger['delete'];
  onRemove?: InputTrigger['remove'];
  onMount?: () => void;
};

const EMPTY_DIALOGUE_ERROR = 'Please add widgets';
const IGNORE_WIDGET_VALIDATION_TYPES = [
  'getemails',
  'contacts_csv',
  'audience',
];

export const InputTriggerCard = ({
  type,
  kind,
  pageId,
  className,
  title,
  channel,
  tags,
  info = '0 contacts',
  active,
  error: errorProp,
  onClick,
  onToggle,
  onDelete,
  onRemove,
  subtitle,
  subtitleImage,
  subtitleStyleLight,
  onMount,
  showActivateAction,
}: Props) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const pageCRUDActions = usePageCRUDActions();
  const automationActions = useAutomationActions();

  const promoters = useSelector((state: StoreState) => state.promoter.entities);
  const promoter = promoters.find(p => p.pageId === pageId && p.kind === kind);

  const keywords =
    Array.isArray(tags) && tags.length
      ? tags
      : promoter?.keywordsEnabled
      ? promoter?.keywords
      : [];

  const chatbotId = useSelector(activeChatbotIdSelector);
  const needsReview = useNeedsReview();
  const showReview = needsReview.pageNeedsReview(pageId);

  const pages = useSelector(selectPages);
  const widgets = useSelector((state: StoreState) => state.widget.entities);

  const getPage = (id?: number) => {
    return pages.find(page => page.id.toString() === id?.toString());
  };

  const page = getPage(pageId);

  const getError = (page?: PageModel) => {
    if (widgets.every(w => w.pageId !== page?.id)) {
      return EMPTY_DIALOGUE_ERROR;
    }
  };

  const error = useMemo(() => {
    return (
      errorProp ||
      (IGNORE_WIDGET_VALIDATION_TYPES.includes(type || '')
        ? ''
        : getError(page))
    );
  }, [page, errorProp, widgets?.length, type]);

  const handleClick = async () => {
    if (pageId && showReview) {
      try {
        await pageCRUDActions.update({
          botId: chatbotId,
          id: pageId,
          needsReview: false,
        } as PageModel);
        automationActions.decreaseReviewRequiredCount();
      } catch (err) {}
    }
    onClick?.();
  };

  useEffect(() => {
    onMount?.();
  }, []);

  const onActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const toggle = () => {
    try {
      setBusy(true);
      onToggle?.(!active);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    try {
      setBusy(true);
      await onDelete?.();
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    try {
      setBusy(true);
      await onRemove?.();
    } finally {
      setBusy(false);
    }
  };

  const menu = [
    onToggle && {
      disabled: !!error,
      children: active ? 'Disable' : 'Enable',
      onClick: toggle,
    },
    onRemove && {
      disabled: !onRemove,
      children: 'Remove from Automation',
      onClick: handleRemove,
    },
    onDelete && {
      disabled: !onDelete,
      children: 'Delete',
      onClick: handleDelete,
    },
  ].filter(Boolean);

  const customIconMap = (type: string) => {
    switch (type) {
      case 'email':
        return <XRayIcon color={!active ? '#8c8c8c' : '#7B3BBB'} />;
      default:
        return null;
    }
  };

  return (
    <div
      className={classNames(className, css.container, {
        [css.inactive]: !active,
        [css.errored]: error,
        [css.busy]: busy,
        [css.open]: open,
      })}
      onClick={handleClick}
    >
      {showReview && (
        <ReviewBadge className={css.badge} withIcon text="Needs review" />
      )}
      <div className={css.titleWrapper}>
        {channel && (
          <ChannelIcon
            channel={channel}
            activeStyle={active}
            className={!active ? css[`inactiveIcon_${channel}`] : ''}
            customMap={{
              email: () => customIconMap('email'),
            }}
          />
        )}
        <div className={css.title}>{title}</div>
      </div>
      {!error && (
        <>
          <div className={css.description}>
            {subtitleImage && (
              <div
                className={classNames(css.subtitleImageWrapper, {
                  [css.disabledSubtitleImageWrapper]: !active,
                })}
              >
                <ImageWithFallback
                  src={subtitleImage}
                  fallbackImage={noImage}
                  alt="Post"
                  className={css.subtitleImage}
                />
              </div>
            )}
            <div className={css.textWrapper}>
              {Array.isArray(keywords) && (
                <span className={css.tags}>
                  {!keywords.find(k => emojis[k])
                    ? keywords?.join(', ')
                    : keywords?.map(tag => {
                        const emojiSrc = emojis[tag];
                        return (
                          <span key={tag} className={css.tag}>
                            {emojiSrc && (
                              <img
                                className={css.emoji}
                                alt={tag}
                                src={emojiSrc}
                              />
                            )}
                          </span>
                        );
                      })}
                </span>
              )}
              {subtitle && (
                <div className={css.subtitleWrapper}>
                  <div
                    className={classNames(css.subtitle, {
                      [css.subtitleLight]: subtitleStyleLight,
                    })}
                  >
                    {subtitle}
                  </div>
                </div>
              )}
            </div>
          </div>

          {showActivateAction && (
            <div className={css.activate}>
              <PoweroffOutlined className={css.activateIcon} />
              <span>Activate trigger</span>
            </div>
          )}
          <div className={css.infoContainer}>
            <div className={css.info}>
              <TeamOutlined className={css.infoIcon} />
              {info}
            </div>
            <Status
              status={active ? 'active' : 'inactive'}
              color={active ? '#389E0D' : '#F5222D'}
              className={classNames(css.status, {
                [css.inactiveStatus]: !active,
              })}
            />
          </div>
        </>
      )}

      {error && <span className={css.error}>{error}</span>}

      {!!menu.length && (
        <span className={css.action} onClick={onActionClick}>
          <Menu
            className={css.menu}
            direction="left"
            offsetX={4}
            onChange={state => setOpen(state.open)}
            menuButton={<MoreOutlined />}
          >
            {menu.map((item, idx) => (
              <MenuItem key={idx} {...item} />
            ))}
          </Menu>
        </span>
      )}
    </div>
  );
};
